import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { filter, takeUntil } from 'rxjs/operators';
import { ControlValueAccessorComponent } from '../../core';
import { DialogService } from '../../services';
import { FIRST_AVAILABLE_STAFF_ID, Staff } from '../../models';
import {
  StaffSelectionDialogComponent,
  StaffSelectionDialogComponentData,
} from './staff-selection-dialog/staff-selection-dialog.component';
import { CommonModule } from '@angular/common';
import { PipesModule } from '../../pipes';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'lib-staff-selection-form',
  templateUrl: './staff-selection-form.component.html',
  styleUrls: ['./staff-selection-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    PipesModule,
    // material design
    MatIconModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StaffSelectionFormComponent),
      multi: true,
    },
  ],
})
export class StaffSelectionFormComponent
  extends ControlValueAccessorComponent
  implements ControlValueAccessor
{
  private _staffs: Staff[];
  @Input() set staffs(value: Staff[]) {
    this._staffs = value || [];
    this.updateSelectedStaff();
  }
  get staffs(): Staff[] {
    return this._staffs;
  }

  public isDisabled = false;

  public selectedStaff: Staff;
  public selectedStaffId: number;

  constructor(private dialogService: DialogService) {
    super();
  }

  public isAbleToSelectStaff(): boolean {
    return !this.isDisabled && this.staffs?.length > 0;
  }

  public openStaffSelectionDialog(): void {
    if (!this.isAbleToSelectStaff()) {
      return;
    }

    const dialogRef = this.dialogService.openDialog(
      StaffSelectionDialogComponent,
      this.dialogService.createDefaultDialogConfig({
        staffs: this.staffs,
        currentSelectedStaffId: this.selectedStaffId,
      } as StaffSelectionDialogComponentData)
    );

    dialogRef
      .afterClosed()
      .pipe(filter((staffId) => !!staffId))
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((staffId) => {
        this.writeValue(staffId);
        this.emitSelectedStaffId();
      });
  }

  private emitSelectedStaffId(): void {
    this._onChange(
      this.selectedStaffId === FIRST_AVAILABLE_STAFF_ID
        ? null
        : this.selectedStaffId
    );
  }

  private updateSelectedStaff(): void {
    this.selectedStaff = this.staffs?.find(
      (s) => s.id === this.selectedStaffId
    );
  }

  // required for NG_VALUE_ACCESSOR
  writeValue(staffId: number) {
    this.selectedStaffId = staffId || FIRST_AVAILABLE_STAFF_ID;
    this.updateSelectedStaff();
  }

  // required for NG_VALUE_ACCESSOR
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
}
